'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { trackEvent } from '@/lib/analytics/counterscale';

/* RailStrip — horizontal scroll surface with edge fades + arrow
   controls.

   Why a client component: the server can't observe scroll position,
   and the arrows / fades depend on it. The actual rail content
   (cards) is composed at server-render time and passed as children,
   so the API surface stays declarative for callers.

   Behavior:
   - Edge fade gradient on whichever side has more content to scroll
     to. Both sides fade when the strip is scrolled mid-way; left
     fades hide at the start, right fades hide at the end.
   - Arrow buttons mirror the same logic — left arrow hides at the
     start, right at the end. Hidden entirely on touch-only devices
     (`@media (hover: none)`) since they're redundant with swipe.
   - Each click scrolls by 80% of the strip's visible width with
     `scroll-behavior: smooth`. Browsers honor scroll-snap-type so
     the rest position lands on a card boundary.

   Used by /design/homepage-rails and /design/homepage-two-deck. The
   parent supplies the cards as children and the .mo-rail-strip
   class is applied internally — callers don't need to know about
   the wrapper plumbing.
*/

export function RailStrip({
  children,
  ariaLabel,
  trackingSlug,
}: {
  children: ReactNode;
  ariaLabel?: string;
  trackingSlug?: string;
}) {
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const advanceFired = useRef(false);

  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;

    const update = () => {
      /* 1px tolerance — sub-pixel scroll positions on retina displays
         can leave scrollLeft at e.g. 0.5 even when visually flush. */
      const start = el.scrollLeft <= 1;
      const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      setAtStart(start);
      setAtEnd(end);
      if (!start && !advanceFired.current && trackingSlug) {
        advanceFired.current = true;
        trackEvent(`rail_${trackingSlug}_advance`);
      }
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    /* If the viewport resizes (or content fills in lazily once
       thumbnails load), re-evaluate at-end since scrollWidth can
       grow. */
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [trackingSlug]);

  const scrollBy = (direction: -1 | 1) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * el.clientWidth * 0.8,
      behavior: 'smooth',
    });
  };

  return (
    <div
      className="mo-rail-wrap"
      data-at-start={atStart || undefined}
      data-at-end={atEnd || undefined}
    >
      <div
        ref={stripRef}
        className="mo-rail-strip"
        role="region"
        aria-label={ariaLabel}
      >
        {children}
      </div>

      <button
        type="button"
        className="mo-rail-arrow mo-rail-arrow-left"
        onClick={() => scrollBy(-1)}
        aria-label="Scroll left"
        tabIndex={atStart ? -1 : 0}
      >
        <ChevronIcon dir="left" />
      </button>
      <button
        type="button"
        className="mo-rail-arrow mo-rail-arrow-right"
        onClick={() => scrollBy(1)}
        aria-label="Scroll right"
        tabIndex={atEnd ? -1 : 0}
      >
        <ChevronIcon dir="right" />
      </button>
    </div>
  );
}

function ChevronIcon({ dir }: { dir: 'left' | 'right' }) {
  /* Inline SVG chevron — single 2px stroke path, currentColor so the
     button's color rule controls it. Lucide-style geometry. */
  const d = dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6';
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
}
